/**
 * Sticker and background catalogues.
 *
 * The SVG files live in `packages/layouts/assets/{stickers,backgrounds}` and are served by the web
 * app at `/assets/{kind}/{id}.svg` (see docs/01-build-plan.md §5.5). `assetUrl` gives every layer
 * the public URL; the absolute filesystem directories (`assetDirs`/`ASSET_DIRS`/`assetPath`) are
 * Node-only and live in `@freealbum/layouts/node`, so this module stays browser safe.
 *
 * Every file is original artwork authored for this project — no third-party clip art.
 */
import type { AssetKind, BackgroundAsset, StickerAsset } from './types';

/** Public URL of an asset. Matches the routes in docs/01-build-plan.md §5.5. */
export function assetUrl(kind: AssetKind, id: string): string {
  return `/assets/${kind}/${id}.svg`;
}

// ---------------------------------------------------------------------------
// stickers
// ---------------------------------------------------------------------------

/** aspect = width / height. 1 = square file, 2 = wide file, 0.5 = tall file. */
const sticker = (id: string, aspect: number, ...tags: string[]): StickerAsset => ({
  id,
  nameKey: `stickers.${id}`,
  tags,
  path: assetUrl('stickers', id),
  aspect,
});

const SQ = 1;
const WIDE = 2;
const TALL = 0.5;

export const STICKERS: readonly StickerAsset[] = [
  // hearts & stars
  sticker('heart', SQ, 'love', 'family', 'wedding', 'baby', 'shape'),
  sticker('heart-outline', SQ, 'love', 'family', 'wedding', 'baby', 'line'),
  sticker('heart-double', SQ, 'love', 'wedding', 'shape'),
  sticker('heart-arrow', SQ, 'love', 'wedding', 'playful'),
  sticker('star', SQ, 'celebration', 'family', 'baby', 'shape'),
  sticker('star-outline', SQ, 'celebration', 'family', 'baby', 'line'),
  sticker('sparkle', SQ, 'celebration', 'wedding', 'baby', 'accent'),
  sticker('confetti-burst', SQ, 'celebration', 'family', 'baby', 'party'),
  // jewish & ceremonial
  sticker('star-of-david', SQ, 'jewish', 'family', 'wedding', 'holiday'),
  sticker('hamsa', SQ, 'jewish', 'family', 'baby', 'folk'),
  sticker('pomegranate', SQ, 'jewish', 'family', 'holiday', 'nature'),
  sticker('kiddush-cup', SQ, 'jewish', 'wedding', 'holiday'),
  sticker('chuppah', SQ, 'jewish', 'wedding', 'ceremony'),
  sticker('rings', SQ, 'wedding', 'ceremony', 'love'),
  sticker('dove', SQ, 'wedding', 'nature', 'peace'),
  sticker('doves-pair', SQ, 'wedding', 'nature', 'love'),
  // ribbons, banners & frames
  sticker('banner', WIDE, 'label', 'family', 'wedding', 'travel', 'baby'),
  sticker('banner-ribbon', WIDE, 'label', 'family', 'wedding', 'celebration'),
  sticker('bunting-flags', WIDE, 'celebration', 'family', 'baby', 'party'),
  sticker('ribbon-bow', SQ, 'gift', 'wedding', 'baby', 'celebration'),
  sticker('ribbon-award', SQ, 'celebration', 'family', 'award'),
  sticker('crown', SQ, 'celebration', 'baby', 'playful'),
  sticker('frame-corner', SQ, 'frame', 'family', 'wedding', 'line'),
  sticker('frame-corner-floral', SQ, 'frame', 'wedding', 'floral', 'line'),
  sticker('frame-oval', SQ, 'frame', 'family', 'wedding', 'vintage'),
  sticker('polaroid', SQ, 'frame', 'family', 'travel', 'photo'),
  sticker('laurel', SQ, 'wedding', 'nature', 'award', 'line'),
  // tape & washi
  sticker('tape-strip', WIDE, 'tape', 'family', 'travel', 'scrapbook'),
  sticker('tape-strip-double', WIDE, 'tape', 'family', 'travel', 'scrapbook'),
  sticker('washi-strip', WIDE, 'tape', 'family', 'baby', 'scrapbook'),
  sticker('washi-dots', WIDE, 'tape', 'baby', 'scrapbook', 'playful'),
  // nature
  sticker('flower-daisy', SQ, 'floral', 'family', 'wedding', 'baby', 'nature'),
  sticker('flower-rose-line', SQ, 'floral', 'wedding', 'line', 'nature'),
  sticker('flower-tulip', SQ, 'floral', 'family', 'nature'),
  sticker('leaf', SQ, 'nature', 'wedding', 'travel'),
  sticker('leaf-branch', SQ, 'nature', 'wedding', 'line'),
  sticker('olive-branch', SQ, 'nature', 'wedding', 'jewish'),
  sticker('palm-tree', SQ, 'travel', 'nature', 'summer'),
  sticker('mountain', SQ, 'travel', 'nature', 'outdoors'),
  sticker('seashell', SQ, 'travel', 'nature', 'summer'),
  sticker('paw-print', SQ, 'family', 'pets', 'playful'),
  sticker('feather-quill', TALL, 'wedding', 'vintage', 'line'),
  // sky & weather
  sticker('sun', SQ, 'travel', 'summer', 'nature', 'family'),
  sticker('cloud', SQ, 'travel', 'baby', 'sky'),
  sticker('rainbow', SQ, 'baby', 'family', 'playful', 'sky'),
  sticker('moon-stars', SQ, 'baby', 'night', 'sky'),
  sticker('snowflake', SQ, 'travel', 'winter', 'nature'),
  // travel
  sticker('airplane', SQ, 'travel', 'journey'),
  sticker('suitcase', SQ, 'travel', 'journey'),
  sticker('camera', SQ, 'travel', 'family', 'photo'),
  sticker('map-pin', SQ, 'travel', 'journey', 'location'),
  sticker('compass', SQ, 'travel', 'journey', 'vintage'),
  sticker('globe', SQ, 'travel', 'journey'),
  sticker('anchor', SQ, 'travel', 'summer', 'nautical'),
  // celebration & food
  sticker('balloon', SQ, 'celebration', 'baby', 'family', 'party'),
  sticker('balloons', SQ, 'celebration', 'baby', 'family', 'party'),
  sticker('cake', SQ, 'celebration', 'family', 'wedding', 'party'),
  sticker('cupcake', SQ, 'celebration', 'baby', 'family', 'party'),
  sticker('candle', SQ, 'celebration', 'family', 'jewish', 'holiday'),
  sticker('gift', SQ, 'celebration', 'family', 'baby', 'party'),
  sticker('coffee-cup', SQ, 'family', 'everyday', 'travel'),
  sticker('wine-glasses', SQ, 'wedding', 'celebration', 'party'),
  sticker('ladder-string-lights', TALL, 'celebration', 'wedding', 'party', 'night'),
  // baby
  sticker('baby-bottle', SQ, 'baby', 'family'),
  sticker('baby-pram', SQ, 'baby', 'family'),
  sticker('baby-onesie', SQ, 'baby', 'family'),
  sticker('pacifier', SQ, 'baby', 'family'),
  // doodles & text
  sticker('arrow-curved', SQ, 'doodle', 'family', 'travel', 'line'),
  sticker('arrow-doodle', SQ, 'doodle', 'family', 'travel', 'line'),
  sticker('speech-bubble', WIDE, 'label', 'family', 'playful'),
  sticker('quote-marks', WIDE, 'label', 'family', 'wedding', 'typographic'),
  sticker('music-note', SQ, 'celebration', 'family', 'party'),
];

const STICKERS_BY_ID = new Map(STICKERS.map((s) => [s.id, s]));

export function getSticker(id: string): StickerAsset | undefined {
  return STICKERS_BY_ID.get(id);
}

/** Stickers carrying every tag in `tags` (empty list = all). */
export function getStickers(tags: string[] = []): StickerAsset[] {
  return STICKERS.filter((s) => tags.every((t) => s.tags.includes(t)));
}

/** Stickers matching ANY of the tags, ordered by how many tags they match. */
export function getStickersByAnyTag(tags: string[]): StickerAsset[] {
  if (tags.length === 0) return [...STICKERS];
  return STICKERS.map((s) => ({ s, n: tags.filter((t) => s.tags.includes(t)).length }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n)
    .map((x) => x.s);
}

// ---------------------------------------------------------------------------
// backgrounds
// ---------------------------------------------------------------------------

const bg = (id: string, mode: 'tile' | 'cover', ...tags: string[]): BackgroundAsset => ({
  id,
  nameKey: `backgrounds.${id}`,
  tags,
  path: assetUrl('backgrounds', id),
  mode,
});

export const BACKGROUNDS: readonly BackgroundAsset[] = [
  bg('dots-fine', 'tile', 'pattern', 'subtle', 'family', 'baby', 'wedding'),
  bg('dots-large', 'tile', 'pattern', 'playful', 'baby', 'family'),
  bg('grid-lines', 'tile', 'pattern', 'subtle', 'family', 'travel'),
  bg('lines-diagonal', 'tile', 'pattern', 'subtle', 'family', 'travel'),
  bg('lines-horizontal', 'tile', 'pattern', 'subtle', 'family'),
  bg('crosshatch', 'tile', 'pattern', 'texture', 'travel', 'family'),
  bg('plus-signs', 'tile', 'pattern', 'playful', 'baby', 'family'),
  bg('linen', 'tile', 'texture', 'subtle', 'wedding', 'family'),
  bg('kraft', 'tile', 'texture', 'paper', 'travel', 'family'),
  bg('paper-grain', 'tile', 'texture', 'paper', 'subtle', 'wedding', 'family'),
  bg('marble', 'tile', 'texture', 'wedding', 'elegant'),
  bg('terrazzo', 'tile', 'texture', 'playful', 'family', 'baby'),
  bg('confetti', 'tile', 'pattern', 'celebration', 'baby', 'family'),
  bg('stars-scatter', 'tile', 'pattern', 'baby', 'night', 'family'),
  bg('waves', 'tile', 'pattern', 'travel', 'summer', 'subtle'),
  bg('waves-bold', 'tile', 'pattern', 'travel', 'summer', 'playful'),
  bg('scallops', 'tile', 'pattern', 'baby', 'playful'),
  bg('chevron', 'tile', 'pattern', 'travel', 'family'),
  bg('herringbone', 'tile', 'pattern', 'texture', 'family', 'wedding'),
  bg('geometric-triangles', 'tile', 'pattern', 'travel', 'family'),
  bg('geometric-hex', 'tile', 'pattern', 'travel', 'family'),
  bg('geometric-diamonds', 'tile', 'pattern', 'family', 'wedding'),
  bg('floral-line', 'tile', 'pattern', 'floral', 'wedding', 'family'),
  bg('floral-sprigs', 'tile', 'pattern', 'floral', 'wedding', 'baby'),
  bg('watercolor-blush', 'cover', 'wash', 'wedding', 'baby', 'soft'),
  bg('watercolor-blue', 'cover', 'wash', 'baby', 'travel', 'soft'),
  bg('watercolor-sage', 'cover', 'wash', 'wedding', 'family', 'soft'),
  bg('gradient-mesh-warm', 'cover', 'gradient', 'family', 'travel', 'soft'),
  bg('gradient-mesh-cool', 'cover', 'gradient', 'travel', 'wedding', 'soft'),
];

const BACKGROUNDS_BY_ID = new Map(BACKGROUNDS.map((b) => [b.id, b]));

export function getBackgroundAsset(id: string): BackgroundAsset | undefined {
  return BACKGROUNDS_BY_ID.get(id);
}

export function getBackgrounds(tags: string[] = []): BackgroundAsset[] {
  return BACKGROUNDS.filter((b) => tags.every((t) => b.tags.includes(t)));
}
